import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Building } from '@app/shared/models/building.interface';

import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
import { GlobalAuthority } from '@app/shared/models/global-authority';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import {
  ManagingCompanyAddUsersDialogComponent
} from '@app/managing-company-administration/managing-company-users/managing-company-add-users-dialog/managing-company-add-users-dialog.component';
import { Role } from '@app/shared/models/role.interface';
import { AddOrUpdateExistingUserPayload } from '@app/shared/models/AddOrUpdateExistingUserPayload';
import { ManagingCompanyAuthorityType } from '@app/shared/models/managing-company-authorities.interface';
import {
  IsAuthorizedForManagingCompanyDirective
} from '@app/shared/directives/is-authorized-for-managing-company.directive';

@Component({
  selector: 'app-managing-company-users-filters',
  standalone: true,
  imports: [
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatInput,
    ReactiveFormsModule,
    MatButton,
    MatIcon,
    IsAuthorizedForManagingCompanyDirective
  ],
  templateUrl: './managing-company-users-filters.component.html',
  styleUrl: './managing-company-users-filters.component.scss'
})
export class ManagingCompanyUsersFiltersComponent implements OnInit, OnChanges {
  @Input() buildings: Building[] = [];
  @Input() dialogBuildings: Building[] = [];
  @Input() showUnassigned = false;
  @Input() managingCompanyId: number;
  @Output() buildingsSelected = new EventEmitter<number[]>();
  @Output() userSearchChanged = new EventEmitter<string>();
  @Output() addOrUpdateExistingUser = new EventEmitter<AddOrUpdateExistingUserPayload>();
  public filterForm: FormGroup;
  protected readonly GlobalAuthority = GlobalAuthority;
  roles: Role[] = [];

  constructor(private fb: FormBuilder, private dialog: MatDialog, private changeDetectorRef: ChangeDetectorRef) {
    this.filterForm = this.fb.group({
      selectedBuildingIds: [[]],
      searchQuery: ['']
    });
  }

  ngOnInit(): void {
    this.filterForm.get('selectedBuildingIds')!.valueChanges.subscribe(() => this.emitSelection());
    this.filterForm.get('searchQuery')!.valueChanges.subscribe((query) => this.userSearchChanged.emit(query));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['managingCompanyId'] && !changes['managingCompanyId'].firstChange) {
      this.filterForm.patchValue({ selectedBuildingIds: [], searchQuery: '' }, { emitEvent: false });
    }

    if (
      (changes['buildings'] || changes['showUnassigned'] || changes['managingCompanyId']) &&
      this.selectedBuildingIds.length === 0
    ) {
      this.selectAll();
      this.emitSelection();
    }
    this.changeDetectorRef.markForCheck();
  }

  get selectedBuildingIds(): number[] {
    return this.filterForm.get('selectedBuildingIds')?.value || [];
  }

  onBuildingSelectionChange(): void {
    const filtered = this.selectedBuildingIds.filter((selectedBuildingId) => selectedBuildingId !== -1);
    this.filterForm.patchValue({ selectedBuildingIds: filtered }, { emitEvent: false });
    this.emitSelection();
  }

  toggleSelectAll(selectAllEvent: MouseEvent): void {
    selectAllEvent.stopPropagation();
    if (this.isAllSelected()) {
      this.filterForm.patchValue({ selectedBuildingIds: [] });
      this.filterForm.patchValue({ searchQuery: '' });
      this.userSearchChanged.emit('');
    } else {
      this.selectAll();
    }
    this.emitSelection();
  }

  isAllSelected(): boolean {
    const totalEntries = this.buildings.length + (this.showUnassigned ? 1 : 0);
    return totalEntries > 0 && this.selectedBuildingIds.length === totalEntries;
  }

  private selectAll(): void {
    const allIds = this.buildings.map((b) => b.id);
    if (this.showUnassigned) allIds.push(0);
    this.filterForm.patchValue({ selectedBuildingIds: allIds });
  }

  private emitSelection(): void {
    this.buildingsSelected.emit([...this.selectedBuildingIds]);
  }

  protected addOrUpdateUser(): void {
    const ref = this.dialog.open(ManagingCompanyAddUsersDialogComponent, {
      width: '720px',
      data: {
        buildings: this.dialogBuildings,
        managingCompanyId: this.managingCompanyId
      }
    });

    ref.afterClosed().subscribe((result) => {
      if (!result) return;
      this.addOrUpdateExistingUser.emit(result);
    });
  }

  protected readonly ManagingCompanyAuthorityType = ManagingCompanyAuthorityType;
}
